import React, { useEffect, useRef, useState } from 'react';
import { Template } from '../types';
import TemplateCard from './TemplateCard';
import { useLayout } from '../context/LayoutContext';
import { supabase } from '../lib/supabase';
import { Loader2 } from 'lucide-react';

interface TemplateGridProps {
  selectedTags: string[];
  selectedTemplateType: string;
  searchQuery?: string;
}

const ITEMS_PER_PAGE = 12;

const TemplateGrid: React.FC<TemplateGridProps> = ({ selectedTags, selectedTemplateType, searchQuery = '' }) => {
  const { gridColumns, templateRefreshKey, searchQuery: contextSearchQuery } = useLayout();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef<HTMLDivElement>(null);

  // Use search query from context if not provided as prop
  const activeSearchQuery = searchQuery || contextSearchQuery;

  const gridColsClass = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
  }[gridColumns];

  const fetchTemplates = async () => {
    if (loading || !hasMore) return;
    
    setLoading(true);
    try {
      console.log('Fetching templates...', { page, selectedTags, selectedTemplateType, searchQuery: activeSearchQuery });

      let query = supabase
        .from('templates')
        .select(`
          id,
          name,
          thumbnail_url,
          template_url,
          template_type,
          json_content,
          figma_html_data,
          template_tags!inner (
            tags!inner (
              name
            )
          )
        `)
        .range(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE - 1)
        .order('created_at', { ascending: false });

      // Filter by template type if not 'all'
      if (selectedTemplateType && selectedTemplateType !== 'all') {
        query = query.eq('template_type', selectedTemplateType);
      }
      // Ensure selectedTags is always an array
      const tagArray = Array.isArray(selectedTags) ? selectedTags : [];

      // Apply search filter if query exists
      let filteredTemplateIds: string[] | null = null;
      
      if (activeSearchQuery.trim()) {
        // Search in template names first
        const { data: nameSearchResults } = await supabase
          .from('templates')
          .select('id')
          .ilike('name', `%${activeSearchQuery.trim()}%`);
        
        // Search in tag names
        const { data: tagSearchResults } = await supabase
          .from('tags')
          .select(`
            id,
            template_tags!inner (
              template_id
            )
          `)
          .ilike('name', `%${activeSearchQuery.trim()}%`);
        
        // Combine results
        const nameIds = nameSearchResults?.map(t => t.id) || [];
        const tagIds = tagSearchResults?.flatMap(tag => 
          tag.template_tags.map((tt: any) => tt.template_id)
        ) || [];
        
        filteredTemplateIds = [...new Set([...nameIds, ...tagIds])];
        
        // If no search results found, return empty
        if (filteredTemplateIds.length === 0) {
          setTemplates([]);
          setHasMore(false);
          setLoading(false);
          return;
        }
      }

      if (tagArray.length > 0) {
        // First get the tag IDs for the selected tag names
        const { data: tagIds } = await supabase
          .from('tags')
          .select('id')
          .in('name', tagArray);

        if (tagIds && tagIds.length > 0) {
          // Get the template IDs that have these tags
          const { data: templateTags } = await supabase
            .from('template_tags')
            .select('template_id')
            .in('tag_id', tagIds.map(tag => tag.id));

          if (templateTags && templateTags.length > 0) {
            // Filter templates by the template IDs we found
            const tagFilteredIds = templateTags.map(tt => tt.template_id);
            
            // If we also have search results, intersect them
            if (filteredTemplateIds) {
              const intersectedIds = tagFilteredIds.filter(id => filteredTemplateIds!.includes(id));
              if (intersectedIds.length === 0) {
                setTemplates([]);
                setHasMore(false);
                setLoading(false);
                return;
              }
              query = query.in('id', intersectedIds);
            } else {
              query = query.in('id', tagFilteredIds);
            }
          } else {
            // If no templates found with these tags, return empty array
            setTemplates([]);
            setHasMore(false);
            setLoading(false);
            return;
          }
        } else {
          // If no matching tags found, return empty array
          setTemplates([]);
          setHasMore(false);
          setLoading(false);
          return;
        }
      } else if (filteredTemplateIds) {
        // Apply search filter if no tag filter
        query = query.in('id', filteredTemplateIds);
      }

      const { data, error: queryError } = await query;
      console.log('Query response:', { data, error: queryError });

      if (queryError) {
        console.error('Error fetching templates:', queryError);
        setError(`Failed to fetch templates: ${queryError.message}`);
        return;
      }

      // Ensure data is an array and not null/undefined
      const templatesArray = Array.isArray(data) ? data : [];

      const formattedTemplates = templatesArray.map(template => ({
        id: template.id,
        title: template.name,
        imageUrl: template.thumbnail_url,
        templateUrl: template.template_url,
        templateType: template.template_type || 'elementor',
        jsonContent: template.json_content,
        figmaHtmlData: template.figma_html_data,
        tags: template.template_tags
          ?.filter(tt => tt?.tags)
          ?.map((tt: any) => tt.tags?.name)
          ?.filter(Boolean) || []
      }));

      console.log('Formatted templates:', formattedTemplates);

      if (page === 0) {
        setTemplates(formattedTemplates);
      } else {
        setTemplates(prev => [...prev, ...formattedTemplates]);
      }
      
      setHasMore(templatesArray.length === ITEMS_PER_PAGE);
      setPage(prev => prev + 1);
    } catch (error) {
      console.error('Error:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset when tags change or when refresh is triggered
    setTemplates([]);
    setPage(0);
    setHasMore(true);
    setError(null);
  }, [selectedTags, selectedTemplateType, templateRefreshKey, activeSearchQuery]);

  useEffect(() => {
    if (page === 0) {
      fetchTemplates();
    }
  }, [page, selectedTags, selectedTemplateType, templateRefreshKey, activeSearchQuery]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !loading && hasMore) {
          fetchTemplates();
        }
      },
      { threshold: 0.1 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => observer.disconnect();
  }, [loading, hasMore]);

  return (
    <div className="w-full p-4 md:p-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {templates.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <p className="text-gray-500 text-lg">No templates found matching your criteria.</p>
          <p className="text-gray-400 text-sm mt-2">Try selecting different tags or clearing your filter.</p>
        </div>
      ) : (
        <>
          <div className={`grid ${gridColsClass} gap-6`}>
            {templates.map((template) => (
              <TemplateCard 
                key={template.id} 
                template={template} 
                onTagClick={(tag) => {
                  if (!selectedTags.includes(tag)) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                selectedTags={selectedTags}
                jsonContent={template.jsonContent}
                templateType={template.templateType}
                figmaHtmlData={template.figmaHtmlData}
              />
            ))}
          </div>
          
          <div 
            ref={loadingRef} 
            className="flex justify-center py-8"
          >
            {loading && (
              <div className="flex items-center space-x-2 text-gray-500">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span>Loading more templates...</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TemplateGrid;